import React, { useState, useRef } from "react";
import { useSearchParams } from "react-router";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Button } from "../components/duvi/Button";
import { Input } from "../components/duvi/Input";
import {
  User,
  KeyRound,
  Camera,
  X,
  Eye,
  EyeOff,
  Shield,
  Loader2,
  Check,
  AlertTriangle,
  Monitor,
  LogOut,
  ExternalLink,
} from "lucide-react";
import { me, type SessionInfo } from "../api/client";
import { useQuery, useMutation } from "../hooks/useApi";
import { useAuth } from "@duvi-agency/auth";
import { testIds } from "@duvi-agency/ui";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";

// ---------------------------------------------------------------------------
// Left‑side navigation items
// ---------------------------------------------------------------------------
const sections = [
  { id: "details", label: "Details", icon: User },
  { id: "security", label: "Security", icon: KeyRound },
] as const;

type SectionId = (typeof sections)[number]["id"];

// ---------------------------------------------------------------------------
// Settings page
// ---------------------------------------------------------------------------
export function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = (searchParams.get("tab") as SectionId) || "details";

  const setSection = (id: SectionId) => {
    setSearchParams({ tab: id });
  };

  return (
    <AppShell breadcrumbs={["Settings"]} activeMenuItem="">
      <div data-testid={testIds.settingsPage} className="flex gap-6">
        {/* Left nav */}
        <nav className="w-48 shrink-0">
          <div className="space-y-1">
            {sections.map((s) => {
              const Icon = s.icon;
              const isActive = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                    isActive
                      ? "bg-duvi-sidebar-active text-duvi-text-primary"
                      : "text-duvi-text-secondary hover:text-duvi-text-primary hover:bg-duvi-elevated"
                  }`}
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  <Icon className="w-4 h-4" />
                  {s.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeSection === "details" && <DetailsSection />}
          {activeSection === "security" && <SecuritySection />}
        </div>
      </div>
    </AppShell>
  );
}

// ===========================================================================
// DETAILS SECTION
// ===========================================================================
function DetailsSection() {
  const { authClient } = useAuth();
  const { data: p, isLoading, refetch } = useQuery(() => me.get());
  const updateNameMutation = useMutation((name: string) => me.updateName(name));
  const updateEmailMutation = useMutation((email: string) =>
    authClient.changeEmail(email),
  );
  const verifyEmailMutation = useMutation((code: string) =>
    authClient.verifyEmailChange(code),
  );
  const uploadAvatarMutation = useMutation((file: File) =>
    me.uploadAvatar(file),
  );
  const removeAvatarMutation = useMutation(() => me.removeAvatar());

  const [name, setName] = useState("");
  const [nameEditing, setNameEditing] = useState(false);
  const [email, setEmail] = useState("");
  const [emailEditing, setEmailEditing] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerification, setShowVerification] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [avatarError, setAvatarError] = useState<string | null>(null);
  // Local override for avatar URL — avoids depending on Cognito attribute propagation.
  const [avatarOverride, setAvatarOverride] = useState<string | null>(null);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const resolvedAvatar = avatarOverride ?? p?.avatar_url;
  const displayAvatar = avatarLoadFailed ? undefined : resolvedAvatar;

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setAvatarError("Please upload a JPEG, PNG, or WebP image.");
      return;
    }
    if (file.size > 1024 * 1024) {
      setAvatarError("Image must be under 1MB.");
      return;
    }

    try {
      const result = await uploadAvatarMutation.mutate(file);
      if (result?.avatar_url) {
        setAvatarLoadFailed(false);
        setAvatarOverride(result.avatar_url);
      }
      refetch();
      window.dispatchEvent(
        new CustomEvent("profile:updated", {
          detail: { avatar_url: result?.avatar_url },
        }),
      );
    } catch {
      /* error in mutation */
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await removeAvatarMutation.mutate(undefined as never);
      setAvatarOverride(null);
      refetch();
      window.dispatchEvent(new Event("profile:updated"));
    } catch {
      /* error in mutation */
    }
  };

  const startEditName = () => {
    setName(p?.name ?? "");
    setNameEditing(true);
    setNameSaved(false);
  };

  const saveName = async () => {
    try {
      await updateNameMutation.mutate(name);
      setNameEditing(false);
      setNameSaved(true);
      refetch();
      window.dispatchEvent(new Event("profile:updated"));
      setTimeout(() => setNameSaved(false), 2000);
    } catch {
      /* error in mutation */
    }
  };

  const startEditEmail = () => {
    setEmail(p?.email ?? "");
    setEmailEditing(true);
    setShowVerification(false);
  };

  const sendEmailChange = async () => {
    try {
      await updateEmailMutation.mutate(email);
      setShowVerification(true);
    } catch {
      /* error in mutation */
    }
  };

  const verifyEmail = async () => {
    try {
      await verifyEmailMutation.mutate(verificationCode);
      setEmailEditing(false);
      setShowVerification(false);
      setVerificationCode("");
      refetch();
    } catch {
      /* error in mutation */
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-duvi-text-secondary" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <h1
        className="text-duvi-text-primary"
        style={{
          fontSize: "var(--text-xl)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        Details
      </h1>

      {/* Profile picture */}
      <Card className="p-6">
        <div
          className="text-duvi-text-primary mb-4"
          style={{
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-semibold)",
          }}
        >
          Profile picture
        </div>
        <div className="flex items-center gap-4">
          <div className="relative group">
            {displayAvatar ? (
              <img
                src={displayAvatar}
                alt="Profile"
                className="w-16 h-16 rounded-full object-cover"
                onError={() => setAvatarLoadFailed(true)}
              />
            ) : (
              <div
                className="w-16 h-16 rounded-full bg-duvi-accent flex items-center justify-center text-white"
                style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: "var(--font-weight-semibold)",
                }}
              >
                {p?.name
                  ? p.name
                      .split(" ")
                      .map((w: string) => w[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)
                  : "?"}
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <Camera className="w-5 h-5 text-white" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid={testIds.settingsAvatarUpload}
              onChange={handleAvatarChange}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadAvatarMutation.isLoading}
            >
              {uploadAvatarMutation.isLoading ? "Uploading..." : "Upload photo"}
            </Button>
            {displayAvatar && (
              <Button
                variant="destructive-outline"
                size="sm"
                onClick={handleRemoveAvatar}
                disabled={removeAvatarMutation.isLoading}
                data-testid={testIds.settingsAvatarRemove}
              >
                {removeAvatarMutation.isLoading
                  ? "Removing..."
                  : "Remove photo"}
              </Button>
            )}
          </div>
        </div>
        <p
          className="text-duvi-text-secondary mt-3"
          style={{ fontSize: "var(--text-2xs)" }}
        >
          JPEG, PNG, or WebP. Max 1MB. The image will be cropped to a square
          from the centre.
        </p>
        {avatarError && (
          <div
            className="text-duvi-destructive mt-2"
            style={{ fontSize: "var(--text-2xs)" }}
          >
            {avatarError}
          </div>
        )}
      </Card>

      {/* Name */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div
            className="text-duvi-text-primary"
            style={{
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-semibold)",
            }}
          >
            Name
          </div>
          {!nameEditing && (
            <button
              onClick={startEditName}
              className="text-duvi-accent hover:underline"
              style={{ fontSize: "var(--text-2xs)" }}
            >
              Edit
            </button>
          )}
        </div>
        {nameEditing ? (
          <div className="space-y-3">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              data-testid={testIds.settingsNameInput}
            />
            {updateNameMutation.error && (
              <div
                className="text-duvi-destructive"
                style={{ fontSize: "var(--text-2xs)" }}
              >
                {updateNameMutation.error.message}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={saveName}
                disabled={!name.trim() || updateNameMutation.isLoading}
                data-testid={testIds.settingsNameSave}
              >
                {updateNameMutation.isLoading ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setNameEditing(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span
              className="text-duvi-text-primary"
              style={{ fontSize: "var(--text-sm)" }}
            >
              {p?.name ?? "—"}
            </span>
            {nameSaved && <Check className="w-4 h-4 text-duvi-success" />}
          </div>
        )}
      </Card>

      {/* Email */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div
            className="text-duvi-text-primary"
            style={{
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-semibold)",
            }}
          >
            Email address
          </div>
          {!emailEditing && (
            <button
              onClick={startEditEmail}
              className="text-duvi-accent hover:underline"
              style={{ fontSize: "var(--text-2xs)" }}
            >
              Edit
            </button>
          )}
        </div>
        {emailEditing ? (
          <div className="space-y-3">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="New email address"
              data-testid={testIds.settingsEmailInput}
            />
            {updateEmailMutation.error && (
              <div
                className="text-duvi-destructive"
                style={{ fontSize: "var(--text-2xs)" }}
              >
                {updateEmailMutation.error.message}
              </div>
            )}
            {!showVerification ? (
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={sendEmailChange}
                  disabled={
                    !email.trim() ||
                    email === p?.email ||
                    updateEmailMutation.isLoading
                  }
                  data-testid={testIds.settingsEmailSave}
                >
                  {updateEmailMutation.isLoading
                    ? "Sending..."
                    : "Send verification"}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEmailEditing(false)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div
                  className="text-duvi-text-secondary"
                  style={{ fontSize: "var(--text-2xs)" }}
                >
                  A verification code has been sent to{" "}
                  <span className="text-duvi-text-primary">{email}</span>. Enter
                  it below to confirm the change.
                </div>
                <Input
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  data-testid={testIds.settingsEmailVerifyCode}
                />
                {verifyEmailMutation.error && (
                  <div
                    className="text-duvi-destructive"
                    style={{ fontSize: "var(--text-2xs)" }}
                  >
                    {verifyEmailMutation.error.message}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={verifyEmail}
                    disabled={
                      verificationCode.length < 6 ||
                      verifyEmailMutation.isLoading
                    }
                    data-testid={testIds.settingsEmailVerifySubmit}
                  >
                    {verifyEmailMutation.isLoading ? "Verifying..." : "Verify"}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEmailEditing(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <span
            className="text-duvi-text-primary"
            style={{ fontSize: "var(--text-sm)" }}
          >
            {p?.email ?? "—"}
          </span>
        )}
      </Card>
    </div>
  );
}

// ===========================================================================
// SECURITY SECTION
// ===========================================================================
function SecuritySection() {
  const { data: profile, refetch: refetchProfile } = useQuery(() => me.get());

  return (
    <div className="space-y-6">
      <h1
        className="text-duvi-text-primary"
        style={{
          fontSize: "var(--text-xl)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        Security
      </h1>
      <SessionsCard />
      <ChangePasswordCard />
      <MFACard
        mfaEnabled={profile?.mfa_enabled ?? false}
        onRemoved={refetchProfile}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active Sessions
// ---------------------------------------------------------------------------
function parseBrowser(ua: string): string {
  if (!ua) return "Unknown";
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("Chrome/") && !ua.includes("Chromium/")) return "Chrome";
  if (ua.includes("Firefox/")) return "Firefox";
  if (ua.includes("Safari/") && !ua.includes("Chrome/")) return "Safari";
  return "Browser";
}

function parseOS(ua: string): string {
  if (!ua) return "";
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Mac OS X") || ua.includes("Macintosh")) return "macOS";
  if (ua.includes("Linux")) return "Linux";
  if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
  if (ua.includes("Android")) return "Android";
  return "";
}

function formatSessionDevice(ua: string): string {
  const browser = parseBrowser(ua);
  const os = parseOS(ua);
  return os ? `${browser} on ${os}` : browser;
}

function formatSessionTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function SessionsCard() {
  const { logout } = useAuth();
  const { data: sessions, isLoading } = useQuery<SessionInfo[]>(() =>
    me.listSessions(),
  );
  const logoutAllMutation = useMutation(() => me.logoutAllSessions());
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleLogoutAll = async () => {
    try {
      await logoutAllMutation.mutate(undefined as never);
      setShowLogoutDialog(false);
      logout();
    } catch {
      /* error in mutation */
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Monitor className="w-4 h-4 text-duvi-text-secondary" />
          <div
            className="text-duvi-text-primary"
            style={{
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-semibold)",
            }}
          >
            Active sessions
          </div>
        </div>
        <Button
          variant="destructive-outline"
          size="sm"
          onClick={() => setShowLogoutDialog(true)}
          disabled={logoutAllMutation.isLoading}
          data-testid={testIds.settingsLogoutAllSessions}
        >
          <LogOut className="w-3.5 h-3.5 mr-1.5" />
          Log out all sessions
        </Button>
      </div>
      <p
        className="text-duvi-text-secondary mb-4"
        style={{ fontSize: "var(--text-2xs)" }}
      >
        These are the devices that are currently logged in to your account. If
        you see a session you don't recognise, log out of all sessions
        immediately and change your password.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-duvi-text-secondary" />
        </div>
      ) : sessions && sessions.length > 0 ? (
        <div className="border border-duvi-border rounded-md overflow-hidden">
          <table className="w-full" data-testid={testIds.settingsSessionsTable}>
            <thead>
              <tr className="bg-duvi-elevated border-b border-duvi-border">
                <th
                  className="text-left px-4 py-2 text-duvi-text-secondary"
                  style={{
                    fontSize: "var(--text-2xs)",
                    fontWeight: "var(--font-weight-medium)",
                  }}
                >
                  Device
                </th>
                <th
                  className="text-left px-4 py-2 text-duvi-text-secondary"
                  style={{
                    fontSize: "var(--text-2xs)",
                    fontWeight: "var(--font-weight-medium)",
                  }}
                >
                  IP address
                </th>
                <th
                  className="text-left px-4 py-2 text-duvi-text-secondary"
                  style={{
                    fontSize: "var(--text-2xs)",
                    fontWeight: "var(--font-weight-medium)",
                  }}
                >
                  Signed in
                </th>
                <th
                  className="text-left px-4 py-2 text-duvi-text-secondary"
                  style={{
                    fontSize: "var(--text-2xs)",
                    fontWeight: "var(--font-weight-medium)",
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s: SessionInfo) => (
                <tr
                  key={s.session_id}
                  className="border-b border-duvi-border last:border-0"
                >
                  <td className="px-4 py-3">
                    <span
                      className="text-duvi-text-primary"
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      {formatSessionDevice(s.user_agent)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {s.ip_address ? (
                      <a
                        href={`https://ipinfo.io/${s.ip_address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-duvi-accent hover:underline font-mono"
                        style={{ fontSize: "var(--text-2xs)" }}
                      >
                        {s.ip_address}
                        <ExternalLink className="w-3 h-3 inline ml-1" />
                      </a>
                    ) : (
                      <span
                        className="text-duvi-text-secondary font-mono"
                        style={{ fontSize: "var(--text-2xs)" }}
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="text-duvi-text-secondary"
                      style={{ fontSize: "var(--text-2xs)" }}
                    >
                      {formatSessionTime(s.created_at)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {s.current && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-duvi-accent/10 text-duvi-accent"
                        style={{ fontSize: "var(--text-2xs)" }}
                      >
                        This device
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p
          className="text-duvi-text-secondary text-center py-6"
          style={{ fontSize: "var(--text-sm)" }}
        >
          No active sessions found.
        </p>
      )}

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent
          className="bg-duvi-surface border-duvi-border"
          data-testid={testIds.settingsLogoutAllDialog}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-duvi-text-primary flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-duvi-warning" />
              Log out of all sessions
            </AlertDialogTitle>
            <AlertDialogDescription className="text-duvi-text-secondary">
              This will log you out of all sessions, including this one. You
              will need to sign in again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={logoutAllMutation.isLoading}
              className="bg-duvi-elevated border-duvi-border text-duvi-text-primary hover:bg-duvi-bg"
            >
              Cancel
            </AlertDialogCancel>
            <button
              onClick={handleLogoutAll}
              disabled={logoutAllMutation.isLoading}
              data-testid={testIds.settingsLogoutAllDialogConfirm}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-duvi-destructive px-4 py-2 text-white hover:bg-duvi-destructive/90 disabled:opacity-50 transition-colors"
              style={{ fontSize: "var(--text-sm)" }}
            >
              {logoutAllMutation.isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Logging out…
                </>
              ) : (
                "Log out all sessions"
              )}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Change Password
// ---------------------------------------------------------------------------
function ChangePasswordCard() {
  const { authClient } = useAuth();
  const changePasswordMutation = useMutation(
    (data: { current_password: string; new_password: string }) =>
      authClient.changePassword(data.current_password, data.new_password),
  );

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);

  const passwordMinLength = 8;
  const passwordMaxLength = 128;
  const passwordTooShort =
    newPassword.length > 0 && newPassword.length < passwordMinLength;
  const passwordsMismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= passwordMinLength &&
    newPassword === confirmPassword &&
    !changePasswordMutation.isLoading;

  const handleSubmit = async () => {
    try {
      await changePasswordMutation.mutate({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      /* error in mutation */
    }
  };

  return (
    <Card className="p-6">
      <div
        className="text-duvi-text-primary mb-4"
        style={{
          fontSize: "var(--text-sm)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        Change password
      </div>
      <div className="space-y-4 max-w-md">
        {/* Current password */}
        <div>
          <label
            className="block text-duvi-text-secondary mb-1"
            style={{ fontSize: "var(--text-2xs)" }}
          >
            Current password
          </label>
          <div className="relative">
            <Input
              type={showCurrent ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              data-testid={testIds.changePasswordCurrent}
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-duvi-text-secondary hover:text-duvi-text-primary"
            >
              {showCurrent ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* New password */}
        <div>
          <label
            className="block text-duvi-text-secondary mb-1"
            style={{ fontSize: "var(--text-2xs)" }}
          >
            New password
          </label>
          <div className="relative">
            <Input
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) =>
                setNewPassword(e.target.value.slice(0, passwordMaxLength))
              }
              autoComplete="new-password"
              data-testid={testIds.changePasswordNew}
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-duvi-text-secondary hover:text-duvi-text-primary"
            >
              {showNew ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          {passwordTooShort && (
            <div
              className="text-duvi-warning mt-1"
              style={{ fontSize: "var(--text-2xs)" }}
            >
              Must be at least {passwordMinLength} characters
            </div>
          )}
        </div>

        {/* Confirm new password */}
        <div>
          <label
            className="block text-duvi-text-secondary mb-1"
            style={{ fontSize: "var(--text-2xs)" }}
          >
            Confirm new password
          </label>
          <div className="relative">
            <Input
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) =>
                setConfirmPassword(e.target.value.slice(0, passwordMaxLength))
              }
              autoComplete="new-password"
              data-testid={testIds.changePasswordConfirm}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-duvi-text-secondary hover:text-duvi-text-primary"
            >
              {showConfirm ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          {passwordsMismatch && (
            <div
              className="text-duvi-destructive mt-1"
              style={{ fontSize: "var(--text-2xs)" }}
            >
              Passwords don't match
            </div>
          )}
        </div>

        {changePasswordMutation.error && (
          <div
            className="text-duvi-destructive"
            style={{ fontSize: "var(--text-2xs)" }}
          >
            {changePasswordMutation.error.message}
          </div>
        )}
        {success && (
          <div
            className="flex items-center gap-2 text-duvi-success"
            style={{ fontSize: "var(--text-2xs)" }}
          >
            <Check className="w-4 h-4" />
            Password changed successfully
          </div>
        )}

        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid={testIds.changePasswordSubmit}
        >
          {changePasswordMutation.isLoading ? "Changing..." : "Change password"}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// MFA Card
// ---------------------------------------------------------------------------
function MFACard({
  mfaEnabled,
  onRemoved,
}: {
  mfaEnabled: boolean;
  onRemoved: () => void;
}) {
  const { authClient } = useAuth();
  const removeMutation = useMutation(
    (data: { password: string; mfa_code: string }) =>
      authClient.removeMFA(data.password, data.mfa_code),
  );

  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const handleDialogOpenChange = (open: boolean) => {
    setShowRemoveDialog(open);
    if (!open) {
      setPassword("");
      setMfaCode("");
      removeMutation.reset();
    }
  };

  const handleRemove = async () => {
    try {
      await removeMutation.mutate({ password, mfa_code: mfaCode });
      handleDialogOpenChange(false);
      onRemoved();
    } catch {
      /* error in mutation */
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-2">
        <div
          className="text-duvi-text-primary"
          style={{
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-semibold)",
          }}
        >
          Multi-factor authentication
        </div>
        <div className="flex items-center gap-2">
          <Shield
            className={`w-4 h-4 ${mfaEnabled ? "text-duvi-success" : "text-duvi-warning"}`}
          />
          <span
            className={mfaEnabled ? "text-duvi-success" : "text-duvi-warning"}
            style={{ fontSize: "var(--text-2xs)" }}
          >
            {mfaEnabled ? "Enabled" : "Not set up"}
          </span>
        </div>
      </div>
      <p
        className="text-duvi-text-secondary mb-4"
        style={{ fontSize: "var(--text-2xs)" }}
      >
        {mfaEnabled
          ? "TOTP authenticator app is configured. Removing MFA will reduce your account security."
          : "MFA adds an extra layer of security to your account. Set it up from the login flow."}
      </p>
      {mfaEnabled && (
        <Button
          variant="destructive-outline"
          size="sm"
          onClick={() => setShowRemoveDialog(true)}
          data-testid={testIds.removeMfaButton}
        >
          Remove MFA
        </Button>
      )}

      <AlertDialog
        open={showRemoveDialog}
        onOpenChange={handleDialogOpenChange}
      >
        <AlertDialogContent
          className="bg-duvi-surface border-duvi-border"
          data-testid={testIds.removeMfaDialog}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-duvi-text-primary flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-duvi-warning" />
              Remove MFA
            </AlertDialogTitle>
            <AlertDialogDescription className="text-duvi-text-secondary">
              Enter your password and a current MFA code to confirm removal. You
              can re-enable MFA later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 px-6 pb-2">
            <div>
              <label
                htmlFor="remove-mfa-password"
                className="block text-duvi-text-secondary mb-1"
                style={{ fontSize: "var(--text-2xs)" }}
              >
                Password
              </label>
              <Input
                id="remove-mfa-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid={testIds.removeMfaPassword}
              />
            </div>
            <div>
              <label
                htmlFor="remove-mfa-code"
                className="block text-duvi-text-secondary mb-1"
                style={{ fontSize: "var(--text-2xs)" }}
              >
                Current MFA code
              </label>
              <Input
                id="remove-mfa-code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="6-digit code"
                maxLength={6}
                data-testid={testIds.removeMfaCode}
              />
            </div>
            {removeMutation.error && (
              <div
                className="text-duvi-destructive"
                style={{ fontSize: "var(--text-2xs)" }}
              >
                {removeMutation.error.message}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={removeMutation.isLoading}
              className="bg-duvi-elevated border-duvi-border text-duvi-text-primary hover:bg-duvi-bg"
            >
              Cancel
            </AlertDialogCancel>
            <button
              onClick={handleRemove}
              disabled={
                !password || mfaCode.length < 6 || removeMutation.isLoading
              }
              data-testid={testIds.removeMfaConfirm}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-duvi-destructive px-4 py-2 text-white hover:bg-duvi-destructive/90 disabled:opacity-50 transition-colors"
              style={{ fontSize: "var(--text-sm)" }}
            >
              {removeMutation.isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Removing…
                </>
              ) : (
                "Remove MFA"
              )}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
